import { Receipt, Buildings, User, CalendarBlank, CurrencyCircleDollar, Bank } from "@phosphor-icons/react";
import { fmtDate, money, qty } from "@/lib/api";

export const T = "#0f766e";

const Icon = ({ as: As }) => (
  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-teal-700 shadow-[0_3px_8px_rgba(13,80,74,0.35)] print:shadow-none">
    <As size={16} weight="fill" />
  </span>
);

const Party = ({ title, icon, p }) => (
  <div className="rounded-md border border-teal-200 bg-teal-50/60 p-4 text-[12px] leading-5 shadow-[0_2px_10px_rgba(13,80,74,0.12)] print:shadow-none">
    <div className="mb-2 flex items-center gap-2">
      <Icon as={icon} />
      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-teal-800">{title}</span>
    </div>
    <div className="text-sm font-bold text-zinc-900">{p.name || "—"}</div>
    <div className="text-zinc-700">{p.address || "—"}</div>
    <div className="text-zinc-700">VD / VKN: {p.tax || "—"}</div>
    <div className="text-zinc-700">Tel: {p.phone || "—"}</div>
  </div>
);

const Cell = ({ icon, label, value, testid }) => (
  <div className="flex items-center gap-2 border border-teal-200 bg-white px-3 py-2">
    <Icon as={icon} />
    <div>
      <div className="text-[10px] font-bold uppercase leading-tight tracking-[0.14em] text-teal-700">{label}</div>
      <div className="font-num text-[13px] font-semibold text-zinc-900 break-words" data-testid={testid}>{value || "—"}</div>
    </div>
  </div>
);

export default function InvoiceSheet({ d, acc = {}, co = {}, testid = "invoice-document" }) {
  const sale = d.kind === "sale";
  const cur = d.currency;
  const firm = { name: co.name || "[Firma Ünvanınız]", address: co.address, tax: [co.tax_office, co.tax_no].filter(Boolean).join(" / "), phone: co.phone };
  const other = { name: acc.name || d.account_name, address: acc.address, tax: [acc.tax_office, acc.tax_no].filter(Boolean).join(" / "), phone: acc.phone };

  return (
    <div className="mx-auto max-w-[210mm] bg-white text-zinc-900 shadow-xl print:max-w-none print:shadow-none" data-testid={testid}>
      <div className="flex flex-col items-start justify-between gap-4 px-[12mm] py-6 text-white sm:flex-row print:flex-row" style={{ backgroundColor: T }}>
        <div className="flex items-center gap-4">
          {co.logo && <img src={co.logo} alt="Logo" className="max-h-16 max-w-[120px] rounded bg-white/95 object-contain p-1.5 shadow-[0_4px_12px_rgba(0,0,0,0.25)] print:shadow-none" data-testid="invoice-logo" />}
          <div className="text-[12px] leading-5">
            <div className="font-display text-xl font-extrabold" data-testid="invoice-firm-name">{firm.name}</div>
            <div className="text-teal-50">{co.address || "—"}</div>
            <div className="text-teal-50">{[firm.tax, co.phone, co.email].filter(Boolean).join(" · ") || "—"}</div>
          </div>
        </div>
        <div className="sm:text-right sm:shrink-0">
          <div className="mb-1 flex items-center gap-2 sm:justify-end">
            <Icon as={Receipt} />
            <span className="font-display text-2xl font-extrabold tracking-tight whitespace-nowrap" data-testid="invoice-title">{sale ? "SATIŞ FATURASI" : "ALIŞ FATURASI"}</span>
          </div>
          <div className="font-num text-sm text-teal-50" data-testid="invoice-no">{d.no}</div>
        </div>
      </div>

      <div className="p-[12mm] pt-6">
        <div className="grid grid-cols-2 gap-0 sm:grid-cols-4 print:grid-cols-4">
          <Cell icon={CalendarBlank} label="Fatura Tarihi" value={fmtDate(d.date)} testid="invoice-date" />
          <Cell icon={CalendarBlank} label="Vade Tarihi" value={fmtDate(d.due_date)} testid="invoice-due" />
          <Cell icon={CurrencyCircleDollar} label="Para Birimi" value={cur === "TRY" ? "TRY" : `${cur} · Kur ${d.rate}`} />
          <Cell icon={Receipt} label="Sipariş No" value={d.order_no} />
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 print:grid-cols-2">
          <Party title="Satıcı" icon={Buildings} p={sale ? firm : other} />
          <Party title="Alıcı" icon={User} p={sale ? other : firm} />
        </div>

        <div className="mt-6 overflow-x-auto print:overflow-visible">
          <table className="w-full min-w-[640px] border-collapse text-[12px]" data-testid="invoice-items">
            <thead>
              <tr className="text-white" style={{ backgroundColor: T }}>
                {["Sıra", "Kod", "Mal / Hizmet", "Miktar", "Birim Fiyat", "İsk.", "KDV", "Tutar"].map((h, i) => (
                  <th key={h} className={`border border-teal-300 px-2 py-2 font-semibold ${i >= 3 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.items.map((it, i) => (
                <tr key={i} className={i % 2 ? "bg-teal-50/50" : ""}>
                  <td className="border border-teal-200 px-2 py-1.5 font-num">{i + 1}</td>
                  <td className="border border-teal-200 px-2 py-1.5 font-num">{it.code}</td>
                  <td className="border border-teal-200 px-2 py-1.5">{it.name}</td>
                  <td className="border border-teal-200 px-2 py-1.5 text-right font-num">{qty(it.qty)} {it.unit}</td>
                  <td className="border border-teal-200 px-2 py-1.5 text-right font-num">{money(it.price, cur)}</td>
                  <td className="border border-teal-200 px-2 py-1.5 text-right font-num">{it.discount ? `%${it.discount}` : "—"}</td>
                  <td className="border border-teal-200 px-2 py-1.5 text-right font-num">%{it.vat_rate}</td>
                  <td className="border border-teal-200 px-2 py-1.5 text-right font-num font-semibold">{money(it.total, cur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:justify-between print:flex-row print:justify-between">
          <div className="max-w-sm text-[12px]">
            {co.iban && (
              <div className="flex items-center gap-2 rounded-md border border-teal-200 bg-teal-50/60 p-3 shadow-[0_2px_10px_rgba(13,80,74,0.12)] print:shadow-none">
                <Icon as={Bank} />
                <div><div className="text-[10px] font-bold uppercase tracking-[0.14em] text-teal-700">Ödeme / IBAN</div><div className="font-num">{co.iban}</div></div>
              </div>
            )}
            {d.notes && <div className="mt-3 border border-teal-200 p-3"><b>Not:</b> {d.notes}</div>}
          </div>
          <div className="w-full font-num text-[13px] sm:max-w-xs print:max-w-xs">
            <div className="flex justify-between border border-teal-200 px-3 py-1.5"><span className="text-teal-800">Ara Toplam</span><span>{money(d.subtotal, cur)}</span></div>
            <div className="flex justify-between border border-t-0 border-teal-200 px-3 py-1.5"><span className="text-teal-800">KDV</span><span>{money(d.vat_total, cur)}</span></div>
            <div className="flex justify-between px-3 py-2.5 text-base font-bold text-white shadow-[0_4px_12px_rgba(13,80,74,0.3)] print:shadow-none" style={{ backgroundColor: T }}>
              <span>Genel Toplam</span><span data-testid="invoice-total">{money(d.total, cur)}</span>
            </div>
            {cur !== "TRY" && <div className="mt-1 text-right text-[11px] text-zinc-500">TL karşılığı: {money(d.total_try)}</div>}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-8 text-[12px]">
          {["Düzenleyen", "Teslim Alan"].map((t) => (
            <div key={t} className="rounded-md border border-teal-200 p-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-teal-700">{t}</div>
              <div className="mt-2">Ad Soyad:</div>
              <div className="mt-8 border-t border-dashed border-teal-300 pt-1 text-center text-zinc-500">İmza / Kaşe</div>
            </div>
          ))}
        </div>
        <div className="mt-6 text-center text-[10px] text-teal-700/70">{co.website || "Bu belge Bizim Cari ile oluşturulmuştur."}</div>
      </div>
    </div>
  );
}
